import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import homeData from "../../data/home.json";
import {
  Globe,
  MoveHorizontal,
  CloudRain,
  Briefcase,
  Users,
  Lightbulb,
  BarChart3,
  GraduationCap,
  ArrowRight,
  Download,
  Award,
  CheckCircle,
  Calendar,
  Handshake,
  Quote,
  ExternalLink,
  ChevronRight,
  Star,
  Zap,
} from "lucide-react";

/* ─────────────────────────────────────────────
 ICON MAP
───────────────────────────────────────────── */
const ICON_MAP = {
  Calendar,
  CheckCircle,
  Users,
  Handshake,
  Globe,
  Award,
  Star,
  Zap,
};

/* ─────────────────────────────────────────────
 ANIMATED COUNTER
───────────────────────────────────────────── */
const AnimatedCounter = ({ value, suffix = "", duration = 2 }) => {
  const ref = useRef(null);
  const hasRun = useRef(false);
  const countObj = useRef({ val: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const trigger = ScrollTrigger.create({
      trigger: el,
      start: "top 85%",
      onEnter: () => {
        if (hasRun.current) return;
        hasRun.current = true;

        gsap.to(countObj.current, {
          val: value,
          duration,
          ease: "power2.out",
          onUpdate: () => {
            el.textContent = Math.round(countObj.current.val) + suffix;
          },
        });
      },
    });

    return () => trigger.kill();
  }, [value, suffix, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      0{suffix}
    </span>
  );
};

/* ─────────────────────────────────────────────
 STAT CARD
───────────────────────────────────────────── */
const StatCard = ({ stat, index }) => {
  const cardRef = useRef(null);
  const IconC = ICON_MAP[stat.icon] || CheckCircle;

  const handleEnter = () => {
    gsap.to(cardRef.current, {
      y: -6,
      scale: 1.03,
      duration: 0.35,
      ease: "power2.out",
    });
  };
  const handleLeave = () => {
    gsap.to(cardRef.current, {
      y: 0,
      scale: 1,
      duration: 0.35,
      ease: "power2.out",
    });
  };

  const colors = [
    {
      bg: "rgba(29,78,216,0.15)",
      icon: "#3b82f6",
      border: "rgba(29,78,216,0.25)",
    },
    {
      bg: "rgba(245,158,11,0.12)",
      icon: "#f59e0b",
      border: "rgba(245,158,11,0.25)",
    },
    {
      bg: "rgba(99,102,241,0.12)",
      icon: "#6366f1",
      border: "rgba(99,102,241,0.25)",
    },
    {
      bg: "rgba(16,185,129,0.12)",
      icon: "#10b981",
      border: "rgba(16,185,129,0.25)",
    },
  ];
  const c = colors[index % colors.length];

  return (
    <div
      ref={cardRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="stat-card-item relative rounded-2xl p-5 cursor-default
 transition-shadow duration-300 hover:shadow-card-hover"
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        backdropFilter: "blur(12px)",
      }}
    >
      {/* Icon */}
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${c.icon}20` }}
      >
        <IconC size={18} style={{ color: c.icon }} />
      </div>

      {/* Value */}
      <p
        className="font-black font-mono leading-none mb-1"
        style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)", color: c.icon }}
      >
        <AnimatedCounter
          value={stat.value}
          suffix={stat.suffix}
          duration={2 + index * 0.3}
        />
      </p>

      {/* Label */}
      <p className="text-sm text-slate-700 leading-snug">{stat.label}</p>

      {/* Decorative corner dot */}
      <div
        className="absolute top-3 right-3 w-2 h-2 rounded-full"
        style={{ background: c.icon, opacity: 0.5 }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
 FEATURE BADGE (floating card)
───────────────────────────────────────────── */
const FeatureBadge = ({ feature, floatDir = 1, delay = 0 }) => {
  const ref = useRef(null);
  const IconC = ICON_MAP[feature.icon] || Star;

  useEffect(() => {
    gsap.to(ref.current, {
      y: floatDir * 10,
      duration: 2.8 + delay,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
      delay,
    });
  }, [floatDir, delay]);

  return (
    <div
      ref={ref}
      className="bg-white rounded-2xl px-4 py-3 flex items-center gap-3
 border border-slate-200 shadow-sm cursor-default"
    >
      <div className="w-8 h-8 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
        <IconC size={14} className="text-secondary-600" />
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-900 leading-none">
          {feature.title}
        </p>
        <p className="text-[10px] text-slate-600 mt-0.5 leading-snug">
          {feature.description}
        </p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 IMAGE PANEL (left column)
───────────────────────────────────────────── */
const ImagePanel = ({ image, features }) => {
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const glowRef = useRef(null);
  const badge1Ref = useRef(null);
  const badge2Ref = useRef(null);
  const frameRef = useRef(null);

  /* Parallax on scroll */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(imgRef.current, {
        yPercent: -12,
        ease: "none",
        scrollTrigger: {
          trigger: wrapperRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        },
      });

      /* Reveal on enter */
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapperRef.current,
          start: "top 75%",
        },
      });

      tl.fromTo(
        frameRef.current,
        { scaleX: 0, transformOrigin: "left center" },
        { scaleX: 1, duration: 0.8, ease: "power3.out" },
      )
        .fromTo(
          wrapperRef.current,
          { clipPath: "inset(0 100% 0 0)" },
          {
            clipPath: "inset(0 0% 0 0)",
            duration: 1,
            ease: "power4.inOut",
          },
          "-=0.4",
        )
        .fromTo(
          badge1Ref.current,
          { x: 30, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6, ease: "back.out(1.7)" },
          "-=0.2",
        )
        .fromTo(
          badge2Ref.current,
          { x: -30, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6, ease: "back.out(1.7)" },
          "-=0.3",
        );

      /* Glow pulse */
      gsap.to(glowRef.current, {
        opacity: 0.7,
        scale: 1.15,
        duration: 3,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    }, wrapperRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="relative flex items-center justify-center lg:justify-start">
      {/* Ambient glow */}
      <div
        ref={glowRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(29,78,216,0.2) 0%, transparent 70%)",
          opacity: 0.5,
        }}
      />

      {/* Decorative frame line */}
      <div
        ref={frameRef}
        className="absolute -top-4 -left-4 w-24 h-24
 border-l-2 border-t-2 border-secondary-400/50 rounded-tl-3xl
 pointer-events-none z-10"
        style={{ transform: "scaleX(0)", transformOrigin: "left center" }}
      />
      <div
        className="absolute -bottom-4 -right-4 w-24 h-24
 border-r-2 border-b-2 border-primary-500/50 rounded-br-3xl
 pointer-events-none z-10"
      />

      {/* Main image wrapper */}
      <div
        ref={wrapperRef}
        className="relative w-full max-w-lg rounded-3xl overflow-hidden
 shadow-2xl"
        style={{
          aspectRatio: "4/5",
          clipPath: "inset(0 100% 0 0)",
        }}
      >
        <img
          ref={imgRef}
          src={image}
          alt="Agora Consulting Team"
          className="w-full h-[115%] object-cover -mt-[7.5%]"
        />

        {/* Image overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)",
          }}
        />

        {/* Bottom text on image */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <p className="text-xs font-semibold tracking-[0.2em] text-secondary-400 uppercase mb-1">
            Based in
          </p>
          <p className="text-white font-bold text-lg leading-tight">
            Addis Ababa, Ethiopia
          </p>
        </div>
      </div>

      {/* Floating badge 1 — top right */}
      <div
        ref={badge1Ref}
        className="absolute -top-5 -right-2 sm:right-4 z-20"
        style={{ opacity: 0 }}
      >
        <FeatureBadge feature={features[0]} floatDir={-1} delay={0} />
      </div>

      {/* Floating badge 2 — bottom left */}
      <div
        ref={badge2Ref}
        className="absolute -bottom-5 -left-2 sm:left-4 z-20"
        style={{ opacity: 0 }}
      >
        <FeatureBadge feature={features[1]} floatDir={1} delay={0.5} />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 TEXT PANEL (right column)
───────────────────────────────────────────── */
const TextPanel = ({ data }) => {
  const panelRef = useRef(null);
  const labelRef = useRef(null);
  const headRef = useRef(null);
  const lineRef = useRef(null);
  const parasRef = useRef([]);
  const statsRef = useRef(null);
  const ctaRef = useRef(null);
  const quoteRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: panelRef.current,
          start: "top 70%",
        },
      });

      /* Label */
      tl.fromTo(
        labelRef.current,
        { x: -30, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
      )
        /* Heading */
        .fromTo(
          headRef.current?.children ?? [],
          { y: 50, opacity: 0, skewY: 2 },
          {
            y: 0,
            opacity: 1,
            skewY: 0,
            stagger: 0.12,
            duration: 0.8,
            ease: "power4.out",
          },
          "-=0.3",
        )
        /* Divider line */
        .fromTo(
          lineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.7,
            ease: "power3.out",
            transformOrigin: "left",
          },
          "-=0.4",
        )
        /* Paragraphs */
        .fromTo(
          parasRef.current,
          { y: 25, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.15,
            duration: 0.7,
            ease: "power3.out",
          },
          "-=0.3",
        )
        /* Stats grid */
        .fromTo(
          statsRef.current?.querySelectorAll(".stat-card-item") ?? [],
          { y: 30, opacity: 0, scale: 0.95 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            stagger: 0.1,
            duration: 0.6,
            ease: "back.out(1.4)",
          },
          "-=0.3",
        )
        /* Quote */
        .fromTo(
          quoteRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.2",
        )
        /* CTA */
        .fromTo(
          ctaRef.current?.children ?? [],
          { y: 20, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.1,
            duration: 0.5,
            ease: "power3.out",
          },
          "-=0.2",
        );
    }, panelRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={panelRef} className="space-y-2">
      {/* Section label */}
      <div ref={labelRef} style={{ opacity: 0 }}>
        {/* <div className="section-label">
          <span className="w-1.5 h-1.5 rounded-full bg-secondary-400" />
          {data.sectionLabel}
        </div> */}
      </div>

      {/* Headline */}
      <div ref={headRef} className="overflow-hidden space-y-1">
        <h2
          className="font-display font-black text-slate-900 leading-tight overflow-hidden"
          style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
        >
          {data.headline}
        </h2>
        <h2
          className="font-display font-black text-gradient-primary leading-tight overflow-hidden"
          style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
        >
          {data.headlineAccent}
        </h2>
      </div>

      {/* Divider line */}
      <div
        ref={lineRef}
        className="h-[2px] w-20 rounded-full"
        style={{
          background: "linear-gradient(90deg, #1d4ed8, #f59e0b)",
          transform: "scaleX(0)",
          transformOrigin: "left",
        }}
      />

      {/* Paragraphs */}
      <div className="space-y-4">
        {[data.description1, data.description2, data.description3].map(
          (para, i) => (
            <p
              key={i}
              ref={(el) => (parasRef.current[i] = el)}
              className="text-slate-600 leading-relaxed text-[15px]"
              style={{ opacity: 0 }}
            >
              {para}
            </p>
          ),
        )}
      </div>

      {/* Stats grid */}
      <div ref={statsRef} className="grid grid-cols-2 gap-3">
        {data.stats.map((stat, i) => (
          <StatCard key={stat.id} stat={stat} index={i} />
        ))}
      </div>

      {/* Quote block */}
      <div
        ref={quoteRef}
        className="relative pl-5 border-l-2 border-secondary-400/60"
        style={{ opacity: 0 }}
      >
        <Quote size={18} className="text-secondary-400/50 mb-2" />
        <p className="text-sm text-slate-700 italic leading-relaxed">
          "Empowering communities through informed solutions, Agora Consulting
          stands as a beacon of progress in the realm of development."
        </p>
      </div>

      {/* CTA buttons */}
      <div ref={ctaRef} className="flex flex-wrap items-center gap-4">
        {/* Download button */}
        <a
          href={data.capacityStatement.href}
          download
          className="magnetic-btn group relative inline-flex items-center gap-2.5
 px-6 py-3.5 bg-secondary-500 hover:bg-secondary-400
 text-dark font-semibold rounded-xl overflow-hidden
 transition-all duration-300 hover:-translate-y-0.5
 hover:shadow-glow-secondary text-sm"
        >
          <span
            className="absolute inset-0 translate-x-[-100%]
 group-hover:translate-x-[100%] transition-transform duration-700
 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          />
          <Download size={15} />
          <span>{data.capacityStatement.buttonText}</span>
          <span className="text-xs opacity-70">
            ({data.capacityStatement.text})
          </span>
        </a>

        {/* Learn more */}
        <Link
          to="/about"
          className="group inline-flex items-center gap-2 text-sm
 font-medium text-slate-600 hover:text-slate-900 :text-slate-200
 transition-colors duration-300"
        >
          <span>Learn More About Us</span>
          <ArrowRight
            size={14}
            className="transition-transform duration-300 group-hover:translate-x-1
 text-primary-500"
          />
        </Link>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 SECTION HEADER (centered top label)
───────────────────────────────────────────── */
const SectionDivider = () => {
  const ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current?.children ?? [],
        { scaleX: 0 },
        {
          scaleX: 1,
          stagger: 0.15,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ref.current,
            start: "top 90%",
          },
        },
      );
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={ref} className="flex items-center gap-3 mb-16 lg:mb-10">
      <div
        className="flex-1 h-px origin-left"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(29,78,216,0.4))",
          transform: "scaleX(0)",
        }}
      />
      <div
        className="w-2 h-2 rounded-full bg-secondary-400"
        style={{ transform: "scaleX(0)" }}
      />
      <div
        className="flex-1 h-px origin-right"
        style={{
          background:
            "linear-gradient(to left, transparent, rgba(29,78,216,0.4))",
          transform: "scaleX(0)",
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
 SCROLL REVEAL WRAPPER
───────────────────────────────────────────── */
const RevealOnScroll = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ref.current,
            start: "top 85%",
          },
        },
      );
    }, ref);
    return () => ctx.revert();
  }, [delay]);

  return (
    <div ref={ref} className={className} style={{ opacity: 0 }}>
      {children}
    </div>
  );
};

/* ─────────────────────────────────────────────
 EXPERTISE TAGS (bottom row)
───────────────────────────────────────────── */
const ExpertiseTags = () => {
  // Map each tag to its respective Lucide icon component
  const tags = [
    { name: "Migration", icon: Globe },
    { name: "Displacement", icon: MoveHorizontal },
    { name: "Climate Change", icon: CloudRain },
    { name: "Policy & Governance", icon: Briefcase },
    { name: "Gender", icon: Users },
    { name: "Entrepreneurship", icon: Lightbulb },
    { name: "M&E", icon: BarChart3 },
    { name: "Capacity Building", icon: GraduationCap },
  ];

  const containerRef = useRef(null);

  useEffect(() => {
    // Select only the child elements that are actual tag elements
    const elements = containerRef.current
      ? containerRef.current.querySelectorAll(".expertise-tag")
      : [];

    const ctx = gsap.context(() => {
      gsap.fromTo(
        elements,
        { y: 20, opacity: 0, scale: 0.9 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          stagger: 0.06,
          duration: 0.5,
          ease: "back.out(1.5)",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 88%",
          },
        },
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex flex-wrap items-center gap-4 justify-center p-4 max-w-4xl mx-auto"
    >
      {tags.map((tag, i) => {
        const Icon = tag.icon;
        return (
          <span
            key={i}
            className="expertise-tag text-xl opacity-0 cursor-default flex items-center gap-8 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 rounded font-medium shadow-sm transition-all duration-200 hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:scale-105 p-5"
          >
            <Icon
              size={36}
              className="text-slate-400 dark:text-slate-500 transition-colors group-hover:text-indigo-500"
            />
            {tag.name}
          </span>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────────────
 MAIN ABOUT SNAPSHOT
───────────────────────────────────────────── */
const AboutSnapshot = () => {
  const { about } = homeData;
  const sectionRef = useRef(null);
  const bgRef = useRef(null);

  /* Background parallax */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(bgRef.current, {
        yPercent: 30,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about-snapshot"
      className="relative overflow-hidden section-py bg-white"
    >
      {/* ── BACKGROUND ── */}

      {/* Animated gradient blob */}
      <div
        ref={bgRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
 radial-gradient(ellipse 60% 50% at 80% 20%, rgba(59,130,246,0.08) 0%, transparent 60%),
 radial-gradient(ellipse 40% 40% at 10% 80%, rgba(245,158,11,0.05) 0%, transparent 55%)
`,
        }}
      />

      {/* Grid pattern */}
      <div className="absolute inset-0 grid-pattern opacity-5 pointer-events-none" />

      {/* Light mode top edge fade */}
      <div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none opacity-100 transition-opacity duration-500"
        style={{
          background:
            "linear-gradient(to bottom, #ffffff 0%, transparent 100%)",
        }}
      />

      {/* Dark mode top edge fade */}
      <div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none opacity-0 transition-opacity duration-500"
        style={{
          background:
            "linear-gradient(to bottom, rgb(15, 23, 42) 0%, transparent 100%)",
        }}
      />

      <div className="container-fluid relative z-10">
        {/* ── SECTION HEADER ── */}
        <SectionDivider />

        {/* ── MAIN GRID ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 xl:gap-24 items-center">
          {/* Left — Image */}
          <ImagePanel image={about.image} features={about.features} />

          {/* Right — Text */}
          <TextPanel data={about} />
        </div>

        {/* ── BOTTOM EXPERTISE TAGS ── */}
        <div className="mt-20 lg:mt-16">
          {/* Label */}
          <RevealOnScroll className="text-center mb-6">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-slate-500">
              Areas of Expertise
            </p>
          </RevealOnScroll>

          {/* Tags */}
          <ExpertiseTags />

          {/* Bottom decorative line */}
          <RevealOnScroll delay={0.3} className="mt-12">
            <div
              className="h-px w-full max-w-md mx-auto"
              style={{
                background:
                  "linear-gradient(to right, transparent, rgba(29,78,216,0.3), rgba(245,158,11,0.3), transparent)",
              }}
            />
          </RevealOnScroll>
        </div>
      </div>

      {/* ── SIDE DECORATIVE ELEMENTS ── */}

      {/* Left vertical text */}
      <div
        className="absolute left-4 top-1/2 -translate-y-1/2 hidden xl:flex
 flex-col items-center gap-3 pointer-events-none"
      >
        <div
          className="w-px h-16"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(59,130,246,0.4))",
          }}
        />
        <p
          className="text-[10px] font-semibold tracking-[0.4em] text-slate-400
 uppercase"
          style={{ writingMode: "vertical-lr" }}
        >
          About Us
        </p>
        <div
          className="w-px h-16"
          style={{
            background:
              "linear-gradient(to top, transparent, rgba(59,130,246,0.4))",
          }}
        />
      </div>

      {/* Right section number */}
      <div
        className="absolute right-6 top-1/3 hidden xl:flex
 flex-col items-center gap-2 pointer-events-none"
      >
        <p
          className="font-mono text-[10px] tracking-widest text-slate-400"
          style={{ writingMode: "vertical-lr" }}
        >
          02 / WHO WE ARE
        </p>
      </div>

      {/* Light mode bottom edge fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none opacity-100 transition-opacity duration-500"
        style={{
          background: "linear-gradient(to top, #ffffff 0%, transparent 100%)",
        }}
      />

      {/* Dark mode bottom edge fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none opacity-0 transition-opacity duration-500"
        style={{
          background:
            "linear-gradient(to top, rgb(15, 23, 42) 0%, transparent 100%)",
        }}
      />
    </section>
  );
};

export default AboutSnapshot;
